import React, { useEffect, useState } from "react";

import { sellerAPI } from "../../services/portalApi";
import { cash, dateRange, longDate } from "../../services/api";

const STATUS = {
  pending_payment: "Awaiting payment", confirmed: "Confirmed", requested: "Awaiting our quote",
  cancelled: "Cancelled", expired: "Hold expired",
};

/** One booking a partner made or was credited with: where things stand, and the guests' details to keep up to date. */
export default function PartnerBooking({ refId, notice, onBack }) {
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { sellerAPI.get(refId).then(setR).catch((e) => setError(e.message)); }, [refId]);

  if (error) return <div className="mk-wrap"><button type="button" className="pt-back" onClick={onBack}>← Your bookings</button><p className="mk-error">{error}</p></div>;
  if (!r) return <div className="mk-wrap"><p className="mk-muted" style={{ minHeight: "60vh" }}>Loading…</p></div>;

  return (
    <div className="mk-wrap mk-wrap--wide">
      <button type="button" className="pt-back" onClick={onBack}>← Your bookings</button>
      {notice && <p className="acc-notice acc-notice--ok" role="status">{notice}</p>}
      <p className="mk-eyebrow">{r.ref}</p>
      <h1 className="mk-title">{r.retreat}</h1>
      <p className="mk-muted">{dateRange(r.arrive, r.depart)} · {r.guests} {r.guests === 1 ? "guest" : "guests"}{r.room ? ` · ${r.room}` : ""}</p>

      <dl className="pt-figures pt-figures--small">
        <div><dt>Status</dt><dd>{STATUS[r.status] || r.status}</dd></div>
        {r.payment && <div><dt>Payment</dt><dd>{r.payment}</dd></div>}
        {r.total !== undefined && <div><dt>Total · paid</dt><dd>{cash(r.total)} · {cash(r.paid)}</dd></div>}
        {r.balanceDue && <div><dt>Balance due</dt><dd>{longDate(r.balanceDue)}</dd></div>}
        <div><dt>Your commission</dt><dd>{r.commissionState === "none" ? "None" : `${cash(r.commission)} ${r.commissionState}`}</dd></div>
      </dl>

      {r.addons.length > 0 && (
        <p className="mk-muted">Add-ons: {r.addons.map((a) => a.name + (a.status === "requested" ? " (requested)" : "")).join(", ")}</p>
      )}

      <h2 className="pt-h2">Guests</h2>
      {!r.editable && <p className="mk-muted">This booking can't be changed any more. Write to us if something needs fixing.</p>}
      {r.people.map((g) => <GuestForm key={g.id} r={r} g={g} onSaved={setR} />)}
    </div>
  );
}

function GuestForm({ r, g, onSaved }) {
  const [f, setF] = useState({ ...g });
  const [state, setState] = useState("");
  const exclusive = new Set(r.diets.filter((d) => d.exclusive).map((d) => d.id));
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setState(""); };
  const toggle = (id) => {
    const has = f.diets.includes(id);
    setF({ ...f, diets: has ? f.diets.filter((x) => x !== id) : exclusive.has(id) ? [id] : [...f.diets.filter((x) => !exclusive.has(x)), id] });
    setState("");
  };
  const save = async (e) => {
    e.preventDefault();
    setState("saving");
    try {
      const fresh = await sellerAPI.updateGuest(r.ref, g.id, {
        first_name: f.first_name, last_name: f.last_name, email: f.email || "", phone: f.phone || "",
        country: f.country || "", diets: f.diets, dietary_notes: f.dietary_notes || "",
      });
      onSaved(fresh);
      setState("Saved.");
    } catch (err) { setState(err.message); }
  };
  const disabled = !r.editable;
  return (
    <form className="mk-card pt-guest-card" onSubmit={save}>
      <p className="bk-label">{g.is_lead ? "Lead guest" : "Guest"}</p>
      <fieldset disabled={disabled} className="pt-fieldset">
        <div className="mk-row">
          <label className="mk-field"><span>First name</span><input value={f.first_name} onChange={set("first_name")} required /></label>
          <label className="mk-field"><span>Last name</span><input value={f.last_name} onChange={set("last_name")} required /></label>
        </div>
        <div className="mk-row">
          <label className="mk-field"><span>Email {g.is_lead ? "" : <em>optional</em>}</span><input type="email" value={f.email || ""} onChange={set("email")} required={g.is_lead} /></label>
          <label className="mk-field"><span>Phone <em>optional</em></span><input value={f.phone || ""} onChange={set("phone")} /></label>
        </div>
        <p className="bk-label pt-diet-label">Food <em>select all that apply</em></p>
        <div className="bk-chips">
          {r.diets.map((d) => (
            <label key={d.id} className={`bk-chip ${f.diets.includes(d.id) ? "is-on" : ""}`}>
              <input type="checkbox" checked={f.diets.includes(d.id)} onChange={() => toggle(d.id)} />{d.name}
            </label>
          ))}
        </div>
        <label className="mk-field mt-2"><span>Allergies and dietary needs <em>optional</em></span>
          <textarea rows={2} maxLength={1000} value={f.dietary_notes || ""} onChange={set("dietary_notes")} />
        </label>
      </fieldset>
      {!disabled && (
        <div className="mk-actions">
          <button className="mk-btn mk-btn--ghost" disabled={state === "saving"}>{state === "saving" ? "Saving…" : "Save guest"}</button>
          {state && state !== "saving" && <span className="acc-saved" role="status">{state}</span>}
        </div>
      )}
    </form>
  );
}
